/**
 * Add Network Corridor Form Composer
 */

import { useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  AddNetworkCorridorFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddNetworkCorridorFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderTextareaControl,
  renderSelectControl,
  renderButtonControl,
  renderMediaUploaderControl,
  type ControlConfig,
} from '@/forms/formRenderFunctions'
import { useSelectOptionsMap } from '@/hooks/useSelectOptions'
import { coerceFieldValue } from '@/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Route as RouteIcon } from 'lucide-react'
import { AppLogger } from '@/lib/logger'
import { useConfig } from '@/contexts/ConfigContext'
import { useToast } from '@/contexts/ToastContext'

const log = AppLogger.getLogger(import.meta)

// ── Types ────────────────────────────────────────────────────────────────────

export type AddNetworkCorridorComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddNetworkCorridorFormData>
  showHeader?: boolean
  submitLabel?: string
  networkCorridorNumber?: number
}

// ── Validation ───────────────────────────────────────────────────────────────

const validateField = (name: keyof AddNetworkCorridorFormData, value: unknown): string | null => {
  const shape = AddNetworkCorridorFormSchema.shape[name]
  if (!shape) return null
  const parsed = shape.safeParse(value)
  return parsed.success ? null : (parsed.error.issues[0]?.message ?? 'Invalid value')
}

// Only the corridor name is required by the backend (networkCorridorName); everything else optional.
const REQUIRED_FIELDS = ['name']
const NUMBER_FIELDS = ['numberOfPotentialSites', 'expectedCorridorSize']

// ── Composer ─────────────────────────────────────────────────────────────────

export function AddNetworkCorridorComposer(props: AddNetworkCorridorComposerProps) {
  const {
    onSuccess, onError, onCancel, initialValues,
    showHeader = true, submitLabel,
    networkCorridorNumber: propCorridorNumber,
  } = props

  const appConfig = useConfig()
  const { toast } = useToast()
  const useMockUpload = appConfig.appEnv === 'development'
  const queryClient = useQueryClient()
  // Holds the uploaded media URLs so we can inject them into the query cache
  // even if the backend doesn't echo them back in the upsert response.
  const pendingMediaLocationsRef = useRef<string[]>([])

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequired = (name: string) => REQUIRED_FIELDS.includes(name)

  const { optionsMap, loadingMap } = useSelectOptionsMap({
    networkPartner: {
      endpoint: 'getOrganizations',
      dataKey: 'organizations',
      valueField: 'orgNumber',
      labelField: 'name',
    },
  })

  const upsertNetworkCorridor = useApiMutation('upsertNetworkCorridor', 'POST', {
    invalidates: ['getNetworkCorridors', 'getNetworkCorridorByNumber'],
    onSuccess: (responseData: any) => {
      // Try to extract corridor data from various response shapes
      const corridorData = responseData?.content?.networkCorridor ?? responseData?.corridor ?? responseData?.networkCorridor ?? responseData
      const corridorNum = Number(corridorData?.networkCorridorNumber ?? corridorData?.corridorNumber ?? 0)

      // Prefer mediaLocations from the response; fall back to what we uploaded
      const responseMedia: string[] = corridorData?.mediaLocations ?? []
      const mediaLocations = responseMedia.length > 0 ? responseMedia : pendingMediaLocationsRef.current

      log.info('upsertNetworkCorridor response', {
        corridorNum,
        responseMediaCount: responseMedia.length,
        pendingMediaCount: pendingMediaLocationsRef.current.length,
        usingPending: responseMedia.length === 0,
      })

      // Inject into the getNetworkCorridor query cache so the detail page
      // shows images immediately without needing the backend to return them.
      if (corridorNum > 0 && mediaLocations.length > 0) {
        queryClient.setQueryData(
          ['api', 'getNetworkCorridorByNumber', { networkCorridorNumber: corridorNum }],
          { networkCorridor: { ...corridorData, mediaLocations } }
        )
        log.info('Injected mediaLocations into getNetworkCorridorByNumber cache', { corridorNum, count: mediaLocations.length })
      }

      pendingMediaLocationsRef.current = []
      onSuccess?.()
    },
    onError: (error) => {
      log.error('Failed to upsert network corridor:', error)
      pendingMediaLocationsRef.current = []
      onError?.(error)
    },
  })

  const corridorNumber = propCorridorNumber ?? initialValues?.networkCorridorNumber

  const form = useAppForm({
    schema: AddNetworkCorridorFormSchema,
    defaultValues: {
      networkCorridorNumber: corridorNumber,
      name: initialValues?.name ?? '',
      description: initialValues?.description ?? '',
      state: initialValues?.state ?? undefined,
      countiesCovered: initialValues?.countiesCovered ?? '',
      zipcodesCovered: initialValues?.zipcodesCovered ?? '',
      numberOfPotentialSites: initialValues?.numberOfPotentialSites ?? ('' as any),
      expectedCorridorSize: initialValues?.expectedCorridorSize ?? ('' as any),
      networkPartner: initialValues?.networkPartner ?? [],
      corridorMap: (initialValues as any)?.corridorMap ?? '',
      corridorPictures: (initialValues as any)?.corridorPictures ?? ([] as any),
    },
    onSubmit: async ({ value }) => {
      const fv = value as AddNetworkCorridorFormData
      const coerced = {
        networkCorridorNumber: corridorNumber,
        name: fv.name?.trim() ?? '',
        description: fv.description?.trim() ?? '',
        state: (fv as any).state || undefined,
        countiesCovered: fv.countiesCovered?.trim() ?? '',
        zipcodesCovered: fv.zipcodesCovered?.trim() ?? '',
        numberOfPotentialSites: fv.numberOfPotentialSites ? Number(fv.numberOfPotentialSites) : undefined,
        expectedCorridorSize: fv.expectedCorridorSize ? Number(fv.expectedCorridorSize) : undefined,
        networkPartner: Array.isArray(fv.networkPartner) ? fv.networkPartner : [],
        corridorMap: (fv as any).corridorMap || undefined,
        corridorPictures: Array.isArray((fv as any).corridorPictures) ? (fv as any).corridorPictures : [],
      }

      const parsed = AddNetworkCorridorFormSchema.safeParse(coerced)
      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as keyof AddNetworkCorridorFormData, (meta) => ({
              ...meta, errors: [issue.message],
            }))
          }
        }
        // Surface WHY the form didn't submit — this block used to return silently, leaving
        // the user with a Save button that appeared to do nothing.
        const first = parsed.error.issues[0]
        toast({
          title: 'Cannot save corridor',
          description: first
            ? `${String(first.path[0] ?? 'Field')}: ${first.message}`
            : 'Please fix the highlighted fields.',
          type: 'error',
        })
        log.error('[NetworkCorridor] validation failed:', parsed.error.issues)
        return
      }

      const apiRequest = transformToApiRequest(parsed.data)
      // Keep the full list (including dev-mode data URLs) for cache injection.
      // The backend request only contains real S3 keys (data: URLs stripped in schema).
      pendingMediaLocationsRef.current = (apiRequest as any)._allMediaLocations ?? []
      const { _allMediaLocations: _dropped, ...backendRequest } = apiRequest as any
      await upsertNetworkCorridor.mutateAsync(backendRequest)
    },
  })

  // ── Per-field renderers ───────────────────────────────────────────────────

  const renderTextField = (control: any) => (
    <form.AppField
      key={control.name}
      name={control.name}
      validators={{
        onChange: ({ value }) => validateField(control.name, coerceFieldValue(control.name, value, NUMBER_FIELDS)) || undefined,
        onBlur:   ({ value }) => validateField(control.name, coerceFieldValue(control.name, value, NUMBER_FIELDS)) || undefined,
      }}
    >
      {(bound) => renderTextControl(
        { name: control.name, label: control.label, placeholder: control.placeholder,
          required: isRequired(control.name), visibility: true, disabled: control.disabled ?? false,
          type: control.inputType === 'number' ? 'number' : 'text' },
        bound
      )}
    </form.AppField>
  )

  const renderTextareaField = (control: any) => (
    <form.AppField
      key={control.name}
      name={control.name}
      validators={{ onBlur: ({ value }) => validateField(control.name, value) || undefined }}
    >
      {(bound) => renderTextareaControl(
        { name: control.name, label: control.label, placeholder: control.placeholder,
          required: isRequired(control.name), visibility: true, disabled: control.disabled ?? false },
        bound
      )}
    </form.AppField>
  )

  const renderSelectField = (control: any) => (
    <form.AppField
      key={control.name}
      name={control.name}
      validators={{ onBlur: ({ value }) => validateField(control.name, value) || undefined }}
    >
      {(bound) => renderSelectControl(
        { name: control.name, label: control.label, placeholder: control.placeholder,
          required: isRequired(control.name), visibility: true, disabled: control.disabled ?? false,
          options: control.selectOptions || [] },
        bound
      )}
    </form.AppField>
  )

  const renderMultiSelectField = (control: any) => {
    const options = optionsMap[control.name] || []
    const isLoading = loadingMap[control.name]

    return (
      <form.AppField key={control.name} name={control.name}>
        {(bound) => {
          const current = ((bound.state.value as any[]) || []).map(Number)
          const toggle = (id: string, checked: boolean) => {
            const numId = Number(id)
            bound.handleChange(checked ? [...current, numId] : current.filter((v) => Number(v) !== numId))
          }

          return (
            <div className="space-y-2">
              <Label>
                {control.label}
                {control.required && <span className="ml-1 text-base font-bold text-destructive">*</span>}
              </Label>
              {isLoading ? (
                <p className="text-sm text-muted-foreground">{control.loadingMessage ?? 'Loading...'}</p>
              ) : options.length === 0 ? (
                <p className="text-sm text-muted-foreground">{control.emptyMessage ?? 'No options available'}</p>
              ) : (
                <div className="rounded-md border p-3 space-y-2" style={{ maxHeight: 192, overflowY: 'auto' }}>
                  {options.map((opt) => (
                    <div key={opt.value} className="flex items-center gap-3 cursor-pointer hover:bg-accent rounded p-2">
                      <Checkbox
                        id={`${control.name}-${opt.value}`}
                        checked={current.includes(Number(opt.value))}
                        onCheckedChange={(c) => toggle(opt.value, c === true)}
                      />
                      <label htmlFor={`${control.name}-${opt.value}`} className="text-sm cursor-pointer flex-1">
                        {opt.label}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              {bound.state.meta?.errors && bound.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">{bound.state.meta.errors[0]}</p>
              )}
            </div>
          )
        }}
      </form.AppField>
    )
  }

  const renderMediaUploaderField = (control: any) => (
    <form.AppField key={control.name} name={control.name}>
      {(bound) => {
        const controlConfig: ControlConfig = {
          name: control.name, label: control.label, placeholder: control.placeholder,
          required: isRequired(control.name), visibility: true, disabled: control.disabled ?? false,
          description: control.description, uploader: control.uploader,
        }
        return renderMediaUploaderControl(controlConfig, bound, corridorNumber?.toString() ?? 'new', useMockUpload)
      }}
    </form.AppField>
  )

  // ── Group dispatcher ──────────────────────────────────────────────────────

  const renderControl = (control: any) => {
    if (control.htmlControl === 'textarea')      return renderTextareaField(control)
    if (control.htmlControl === 'mediaUploader') return renderMediaUploaderField(control)
    if (control.htmlControl === 'select' && control.controlType !== 'multi-select') return renderSelectField(control)
    if (control.name === 'networkPartner' || control.controlType === 'multi-select') return renderMultiSelectField(control)
    return renderTextField(control)
  }

  const renderFormContent = () =>
    groups.map((group) => {
      const controls = controlsByGroup.get(group.formGroupName) || []
      if (controls.length === 0) return null
      return (
        <div key={group.formGroupName} className="space-y-4">
          {(group as any).formGroupLabel && (
            <h3 className="text-sm font-semibold text-foreground">{(group as any).formGroupLabel}</h3>
          )}
          {controls.map(renderControl).filter(Boolean)}
        </div>
      )
    })

  // ── Render ────────────────────────────────────────────────────────────────

  const formBody = (
    <>
      {renderFormContent()}

      <div className="pt-4 flex justify-center gap-3">
        {renderButtonControl({
          label: FORM_CONFIG.cancelButton?.label ?? '',
          variant: 'outline',
          onClick: onCancel,
          visibility: !!onCancel,
          type: 'button',
        })}
        {renderButtonControl(
          {
            label: upsertNetworkCorridor.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? '')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? ''),
            variant: 'default',
            type: 'submit',
          },
          { SubmitButton: form.SubmitButton }
        )}
      </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <RouteIcon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

// Re-exports
export {
  AddNetworkCorridorFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddNetworkCorridorFormData,
} from './schema'
